import { useEffect, useMemo, useRef, useState } from "react";
import { Combobox, Dialog } from "@headlessui/react";
import { ArrowRightIcon, LightningBoltIcon, SearchIcon, XIcon } from "@heroicons/react/solid";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { IS_CHAT, IS_LIVE_TRACKING, ROLE, TOKEN_NAME } from "../../config";
import { useOnboardingContext } from "../../context/OnboardingContext";
import { getAvailableEntries, searchEntries } from "./searchCatalog";
import { dispatchCommand } from "./dispatchCommand";
import "./globalSearch.css";

const cookies = new Cookies();

export default function GlobalSearch({ onOpen }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingEntry, setPendingEntry] = useState(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, isCheckingAccess, companyCompletionStatus } = useOnboardingContext();
  const role = cookies.get(ROLE);
  const isChat = cookies.get(IS_CHAT) === "true";
  const isLiveTracking = cookies.get(IS_LIVE_TRACKING) === "true";
  const enabled = Boolean(cookies.get(TOKEN_NAME)) && hasAccess && !isCheckingAccess;
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
  const entries = useMemo(() => getAvailableEntries({
    role, hasAccess: enabled, isCheckingAccess, companyCompletionStatus,
    features: { chat: isChat, liveTracking: isLiveTracking },
  }), [role, enabled, isCheckingAccess, companyCompletionStatus, isChat, isLiveTracking]);
  const results = searchEntries(entries, query);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!enabled || event.repeat || event.isComposing || event.altKey || event.shiftKey) return;
      if ((isMac ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === "k") {
        // Existing page dialogs own focus until dismissed. Opening another
        // focus trap over a MUI modal would make both dialogs unusable.
        if (!open && document.querySelector('.MuiModal-root, [role="dialog"]:not(.global-search-dialog), [aria-modal="true"]:not(.global-search-dialog)')) return;
        event.preventDefault();
        onOpen?.();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, isMac, onOpen, open]);

  useEffect(() => {
    setOpen(false);
    setQuery("");
    setPendingEntry(null);
  }, [location.key, enabled, role, companyCompletionStatus]);

  // Let the palette unmount and release its focus trap before opening another modal.
  useEffect(() => {
    if (!pendingEntry || open) return;
    const frame = requestAnimationFrame(() => {
      if (entries.some((entry) => entry.id === pendingEntry.id)) {
        dispatchCommand(pendingEntry, location, navigate);
      }
      setPendingEntry(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingEntry, open, entries, location, navigate]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="global-search-trigger"
        disabled={!enabled}
        onClick={() => { onOpen?.(); setOpen(true); }}
        aria-label="Search pages or actions"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts={isMac ? "Meta+k" : "Control+k"}
      >
        <SearchIcon className="h-5 w-5 shrink-0" />
        <span className="global-search-trigger-label">Search pages or actions…</span>
        <kbd className="global-search-shortcut">{isMac ? "⌘K" : "Ctrl K"}</kbd>
      </button>

      <Dialog open={open && enabled} onClose={close} initialFocus={inputRef} className="global-search-dialog">
        <div className="global-search-backdrop" aria-hidden="true" />
        <div className="global-search-position" onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}>
          <Dialog.Panel className="global-search-panel">
            <Dialog.Title className="sr-only">Search pages and quick actions</Dialog.Title>
            <Combobox value={null} onChange={(entry) => {
              if (!entry) return;
              setPendingEntry(entry);
              close();
            }} nullable>
              <div className="global-search-input-row">
                <SearchIcon className="h-6 w-6 shrink-0 text-gray-400" />
                <Combobox.Input
                  ref={inputRef}
                  className="global-search-input"
                  placeholder="Search pages or actions…"
                  aria-label="Search pages or actions"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="button" onClick={close} aria-label="Close search" className="global-search-close">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <Combobox.Options static className="global-search-results">
                {[
                  ["action", "Quick Actions"],
                  ["page", "Pages"],
                ].map(([type, title]) => {
                  const group = results.filter((entry) => entry.type === type);
                  if (!group.length) return null;
                  return (
                    <li key={type} role="presentation">
                      <div className="global-search-group-title" id={`global-search-${type}`}>{title}</div>
                      <ul role="group" aria-labelledby={`global-search-${type}`}>
                        {group.map((entry) => (
                          <Combobox.Option key={entry.id} value={entry} className={({ active }) =>
                            `global-search-option ${active ? "global-search-option-active" : ""}`}>
                            <span className="global-search-result-icon">
                              {entry.icon || (type === "action" ? <LightningBoltIcon className="h-5 w-5" /> : <ArrowRightIcon className="h-5 w-5" />)}
                            </span>
                            <span className="flex-1">{entry.label}</span>
                            <span className="global-search-result-type">{type === "action" ? "Action" : "Page"}</span>
                            <ArrowRightIcon className="h-4 w-4 shrink-0" />
                          </Combobox.Option>
                        ))}
                      </ul>
                    </li>
                  );
                })}
                {!results.length && (
                  <li role="presentation" className="global-search-empty">
                    <p className="font-medium text-gray-700">No results found</p>
                    <p className="mt-1 text-sm text-gray-500">Try a page name or an action like “create department”.</p>
                  </li>
                )}
              </Combobox.Options>
            </Combobox>
            <div className="global-search-footer">
              <span><kbd>↑</kbd> <kbd>↓</kbd> to navigate</span>
              <span><kbd>↵</kbd> to open</span>
              <span><kbd>Esc</kbd> to close</span>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
